import { Component, ComponentFactoryResolver, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { CustomerIdentificationType } from '@max/customer/models/enums/customer-identification-type.enum';
import { CustomerSearchResultModel } from '@max/customer/search/models/customer-search-result.model';
import { CustomerService } from '@max/customer/shared/customer.service';
import { Jurisdiction } from '@max/foundation/models/enums/jurisdiction.enum';
import { filter, first, tap } from 'rxjs/operators';
import { MvdModalService } from '../../services/modal/mvd-modal.service';
import { SearchResultService } from '../search/search-results/search-result.service';
import { CustomerSearchViewContext, CustomerSearchViewModalComponent } from './search-modal.component';

@Component({
    selector: 'customer-search-bar',
    templateUrl: './customer-search-bar.html'
})
export class CustomerSearchBarComponent implements OnInit {
    @Input() label: string;
    @Input() placeholder: string;
    @Input() required = true;
    @Input() customerNumberId: number;
    @Input() autoHideSearch: boolean;
    @Input() readOnly: boolean;
    @Input() hideUntilLoaded = false;
    @Input() includeDeceased = true;
    @Output() onItemSelected = new EventEmitter<any>();
    @Output() onItemRemoved = new EventEmitter<any>();
    @Output() onResultsReceived = new EventEmitter<any>();

    public hide = false;
    public jurisdictions: Jurisdiction = new Jurisdiction();
    public results: CustomerSearchResultModel[];
    private customerIdentificationTypes: CustomerIdentificationType = new CustomerIdentificationType();

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private injector: Injector,
        private searchResultService: SearchResultService,
        private modalService: MvdModalService,
        private customerService: CustomerService) {}

    async ngOnInit() {
        this.searchResultService.receiveContext(this.componentFactoryResolver, this.injector);
        this.autoHideSearch = this.autoHideSearch || false;
        this.placeholder = this.placeholder || 'Search by: SSN, DL# or Name';
        if (this.hideUntilLoaded) {
            this.hide = true;
        }

        if (this.customerNumberId) {
            this.loadResults(this.customerNumberId);
        }
    }

    onSelect(customer: any) {
        this.onItemSelected.emit(customer);
    }

    // TODO: currently, parent components which embed CustomerSearchBarComponent are calling "onRemove" themselves.
    // This is not ideal. A parent component should provide information to child components via model binding.
    // An alternative approach would be to implement OnChanges so that, when this.customerNumberId becomes falsy,
    // CustomerSearchBarComponent can call this.onRemove() itself.
    onRemove() {
        this.onItemRemoved.emit(this.customerNumberId);
        this.results = undefined;
    }

    receiveResults(data: any) {
        this.onResultsReceived.emit(data);

        if (data && Array.isArray(data)) {
            if (data.length == 1) {
                const customerNumber = data[0] && data[0].customerNumber;

                if (customerNumber) {
                    this.loadResults(customerNumber);
                }
            } else if (data.length == 0) {
                this.onSelect(null);
                this.results = [];
            } else {
                this.popupAdvancedSearchModal({ results: data });
            }
        }
    }

    popupAdvancedSearchModal(results = undefined) {
        const data = new CustomerSearchViewContext(results, this.includeDeceased);
        const dialogRef = this.modalService.open(CustomerSearchViewModalComponent, data);

        dialogRef.afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.loadResults(result));
    }

    private loadResults(customerNumber: number) {
        this.customerService.getCustomer(customerNumber)
            .pipe(
                first(),
                tap(() => this.hide = false),
                filter(customerResult => !!customerResult)
            )
            .subscribe(
                customerResult => this.populateCustomer(customerResult, customerNumber),
                err => console.log(err)
            );
    }

    private populateCustomer(customerResult: any, customerNumber: any) {
        const legacySequenceNumber = customerResult.identifications && customerResult.identifications.find(x => x.customerIdentificationTypeId == this.customerIdentificationTypes.LegacySequenceNumber.id);
        const legacyCustomerNumber = customerResult.identifications && customerResult.identifications.find(x => x.customerIdentificationTypeId == this.customerIdentificationTypes.LegacyCustomerNumber.id);
        const credentialNumber = customerResult.identifications &&
            (customerResult.identifications.find(x => x.customerIdentificationTypeId == this.customerIdentificationTypes.ArizonaDriverLicense.id) ||
                customerResult.identifications.find(x => x.customerIdentificationTypeId == this.customerIdentificationTypes.ArizonaIDNumber.id) ||
                customerResult.identifications.find(x => x.customerIdentificationTypeId == this.customerIdentificationTypes.OutofStateDriverCredential.id) ||
                customerResult.identifications.find(x => x.customerIdentificationTypeId == this.customerIdentificationTypes.OutofStateIdentificationCredential.id));

        const result: CustomerSearchResultModel = new CustomerSearchResultModel({
            customerNumber: customerNumber,
            customerTypeId: customerResult.customerTypeId,
            customerStatusTypeId: customerResult.statusTypeId,
            legacyCustomerNumber: legacyCustomerNumber && legacyCustomerNumber.customerIdentificationValue,
            customerName: customerResult.nameDetail && customerResult.nameDetail.displayName,
            displayAddress: this.formatAddress(customerResult.mailingAddress),
            legacySequenceNumber: legacySequenceNumber && legacySequenceNumber.customerIdentificationValue,
            credentialNumberDisplayValue: credentialNumber ? (this.customerIdentificationTypes.FromId(credentialNumber.customerIdentificationTypeId).displayValue + ': ' + credentialNumber.customerIdentificationValue) : (this.customerIdentificationTypes.FromId(this.customerIdentificationTypes.CustomerID.id).displayValue + ': ' + customerNumber)
        });

        if (customerResult.biometric) {
            if (customerResult.biometric.isBiometricRedacted) {
                result.isBiometricRedacted = customerResult.biometric.isBiometricRedacted;
            }

            if (customerResult.biometric.dateOfBirth) {
                result.dateOfBirth = customerResult.biometric.dateOfBirth;
            }
        }

        if (!result.customerName) {
            result.customerName = customerResult.organizationDetails && customerResult.organizationDetails.displayName;
        }

        this.results = [result];
    }

    private formatAddress(address): string {
        let addressString = '';
        if (address) {
            let jurisdiction = address.jurisdictionId ? this.jurisdictions.FromId(address.jurisdictionId).code + ' ' : '';

            addressString = this.addCommaOrEmpty(address.addressLine1) +
				this.addCommaOrEmpty(address.addressLine2) +
				this.addCommaOrEmpty(address.city) +
				jurisdiction +
				this.formatZip(address.postalZipCode);
        }
        return addressString;
    }

    private addCommaOrEmpty(str: string): string {
        return str ? `${str}, ` : '';
    }

    private formatZip(zip: string) {
        var result = zip;

        if (zip && zip.length > 5) {
            result = zip.substr(0, 5);
        }
        return result;
    }
}
